import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { LightedBorder } from '@sorare/core/src/atoms/ui/LightedBorder';
import { ANY_SPORT_ESSENCE_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { getRewardImageUrl } from '@sorare/core/src/lib/rewards';

import type { RaritySwitcher_cardsShardChest } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  position: relative;
  padding-bottom: var(--double-and-a-half-unit);
`;

const InnerWrapper = styled(Horizontal)`
  align-self: stretch;
  justify-self: stretch;
  position: relative;
  z-index: 1;
`;

const ChevronLeftBold = styled(ChevronRightBold)`
  transform: rotate(180deg);
`;

const ShardsBalance = styled(Vertical)`
  min-width: 50px;
`;

const ShardsIcon = styled.img`
  object-fit: contain;
  user-select: none;
`;
const BLOCHAIN_RARITIES = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
] as const;

type Props = {
  rarity: (typeof BLOCHAIN_RARITIES)[number];
  shardsChest: RaritySwitcher_cardsShardChest;
};

export const RaritySwitcher = ({ rarity, shardsChest }: Props) => {
  const rarityIndex = BLOCHAIN_RARITIES.indexOf(rarity);
  const { generateSportContextPath } = useSportContext();

  return (
    <Wrapper
      style={
        { '--lighted-border-color': getRarityColor(rarity) } as CSSProperties
      }
    >
      <LightedBorder />
      <InnerWrapper gap={1.5}>
        <IconButton
          size="small"
          color="quaternary"
          disableDebounce
          disabled={rarityIndex === 0}
          to={
            rarityIndex === 0
              ? undefined
              : generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
                  params: {
                    type: BLOCHAIN_RARITIES[rarityIndex - 1],
                  },
                })
          }
        >
          <ChevronLeftBold />
        </IconButton>
        <ShardsBalance gap={1.5} center>
          <ShardsIcon
            src={getRewardImageUrl('shard', rarity)}
            alt=""
            height={48}
          />
          <BrandM>{shardsChest?.cardShardsCount || 0}</BrandM>
        </ShardsBalance>
        <IconButton
          size="small"
          color="quaternary"
          disableDebounce
          disabled={rarityIndex === BLOCHAIN_RARITIES.length - 1}
          to={
            rarityIndex === BLOCHAIN_RARITIES.length - 1
              ? undefined
              : generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
                  params: {
                    type: BLOCHAIN_RARITIES[rarityIndex + 1],
                  },
                })
          }
        >
          <ChevronRightBold />
        </IconButton>
      </InnerWrapper>
    </Wrapper>
  );
};

RaritySwitcher.fragments = {
  cardsShardChest: gql`
    fragment RaritySwitcher_cardsShardChest on CardShardsChest {
      id
      cardShardsCount
    }
  ` as TypedDocumentNode<RaritySwitcher_cardsShardChest>,
};
